import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import BookCard from '../../components/BookCard';
import SearchBar from '../../components/SearchBar';
import styles from './Discover.module.css';
import { API_BASE_URL, resolveCoverUrl } from '../../services/api';
import { SearchIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import stylesHome from './Home.module.css';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: null, label: '全部状态' },
  { value: 'ongoing', label: '连载中' },
  { value: 'completed', label: '已完结' },
];

const Discover = () => {
  const [books, setBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([{ id: null, name: '全部' }]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // 获取分类数据
  const fetchCategories = async () => {
      const response = await axios.get(`${API_BASE_URL}/api/categories/`);
    const list = Array.isArray(response.data)
      ? response.data.map((cat) => ({ id: cat.id, name: cat.name }))
      : [];
    setCategories([{ id: null, name: '全部' }, ...list]);
  };

  // 获取小说数据（分页）
  const fetchNovels = async (targetPage, term, categoryId, statusValue) => {
    const skip = (targetPage - 1) * PAGE_SIZE;
    const params = { skip, limit: PAGE_SIZE };
    if (term) params.search = term;
    if (categoryId) params.category_id = categoryId;
    if (statusValue === 'completed') params.is_completed = true;
    else if (statusValue === 'ongoing') params.is_completed = false;

    const response = await axios.get(`${API_BASE_URL}/api/novels/`, { params });
    const novelsData = Array.isArray(response.data)
      ? response.data.map((novel) => ({
        id: novel.id,
        title: novel.title,
        author: novel.author,
        description: novel.description,
        cover_image_url: resolveCoverUrl(novel.cover_image_url),
        is_completed: novel.is_completed ?? false,
      }))
      : [];

    // 判断是否还有更多（如果返回数量小于页大小，则认为没有更多）
    setHasMore(novelsData.length === PAGE_SIZE);
    // 每次只显示当前页数据
      setBooks(novelsData);
  };

  // 初始化数据（加载分类）
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await fetchCategories();
      } catch (err) {
        console.error('加载数据失败:', err);
        setError('加载数据失败');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 当 URL 搜索参数变化时，重新拉取第一页
  useEffect(() => {
    const searchParam = searchParams.get('search') || '';
    const categoryParam = searchParams.get('category');
    const categoryId = categoryParam ? Number(categoryParam) : null;
    const statusParam = searchParams.get('status') || null;

    setSearchTerm(searchParam);
    setSelectedCategory(Number.isFinite(categoryId) ? categoryId : null);
    setSelectedStatus(statusParam);
    setPage(1);
    setHasMore(true);
    setLoading(true);

    fetchNovels(1, searchParam, Number.isFinite(categoryId) ? categoryId : null, statusParam)
      .catch((err) => {
        console.error('加载数据失败:', err);
        setError('加载数据失败');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (term) => {
    const normalized = typeof term === 'string' ? term.trim() : '';
    const nextParams = new URLSearchParams(searchParams);
    if (normalized) {
      nextParams.set('search', normalized);
    } else {
      nextParams.delete('search');
    }
    setSearchParams(nextParams);
  };

  const handleCategoryChange = (categoryId) => {
    const nextParams = new URLSearchParams(searchParams);
    if (categoryId) {
      nextParams.set('category', String(categoryId));
    } else {
      nextParams.delete('category');
    }
    setSearchParams(nextParams);
  };

  const handleStatusChange = (statusValue) => {
    const nextParams = new URLSearchParams(searchParams);
    if (statusValue) {
      nextParams.set('status', statusValue);
    } else {
      nextParams.delete('status');
    }
    setSearchParams(nextParams);
  };

  const handleNextPage = async () => {
    if (!hasMore || loading) return;
    const nextPage = page + 1;
    setLoading(true);
    try {
      await fetchNovels(nextPage, searchTerm, selectedCategory, selectedStatus);
      setPage(nextPage);
    } catch (err) {
      console.error('加载更多失败:', err);
      setError('加载更多失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = async () => {
    if (page <= 1 || loading) return;
    const prevPage = page - 1;
    setLoading(true);
    try {
      await fetchNovels(prevPage, searchTerm, selectedCategory, selectedStatus);
      setPage(prevPage);
      setHasMore(true); // 往前翻页后，下一页按钮重新可用
    } catch (err) {
      console.error('加载失败:', err);
      setError('加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.discover}>
      <div className={styles.discoverHeader}>
        <form className={stylesHome.heroSearch} onSubmit={(e) => { e.preventDefault(); handleSearch(searchTerm); }}>
          <span className={stylesHome.heroSearchIcon}>
            <SearchIcon size={18} />
          </span>
          <input
            className={stylesHome.heroSearchInputWide}
          placeholder="搜索书名、作者..."
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
        />
        </form>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.categoriesFilter}>
          {categories.map((cat) => (
            <button
              key={cat.id ?? 'all'}
              className={`${styles.categoryBtn} ${
                selectedCategory === cat.id ? styles.active : ''
              }`}
              onClick={() => handleCategoryChange(cat.id)}
              disabled={loading}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className={styles.statusFilter}>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value ?? 'all-status'}
              className={`${styles.statusBtn} ${
                selectedStatus === opt.value ? styles.active : ''
              }`}
              onClick={() => handleStatusChange(opt.value)}
              disabled={loading}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.booksGrid}>
        {loading && (
          <div className={styles.discoverLoadingInline}>
            <LoadingSpinner text="加载中..." />
          </div>
        )}
        {!loading && books.length > 0 ? (
          books.map(book => (
            <BookCard 
              key={book.id} 
              book={book}
              showStatus={true}
              from="/discover"
            />
          ))
        ) : !loading ? (
          <div className={styles.noBooks}>
            <p>没有找到符合条件的小说</p>
            {searchTerm && (
              <button 
                className={styles.clearSearchBtn}
                onClick={() => handleSearch('')}
              >
                清除搜索
              </button>
            )}
          </div>
        ) : null}
      </div>

      <div className={styles.paginationBar}>
        <button
          type="button"
          className={styles.pageBtn}
          onClick={handlePrevPage}
          disabled={loading || page <= 1}
        >
          上一页
        </button>
        <span className={styles.pageInfo}>第 {page} 页</span>
        <button
          type="button"
          className={styles.pageBtn}
          onClick={handleNextPage}
          disabled={loading || !hasMore}
        >
          下一页
        </button>
      </div>

      <div className={styles.discoverStats}>
        <p>共找到 {books.length} 本小说</p>
      </div>
    </div>
  );
};

export default Discover;
